import { RewardUnlockStrategy } from '@brandclub/types';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, Icon, styled, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { OutOfStockPill } from '../../BrandclubLanding/components/OutOfStockPill/OutOfStockPill';
import { RewardPillWithText } from '../../BrandclubLanding/components/RewardPill/RewardPillWithText';
import { CampaignType, ProductRewardSearchResult } from '../../types';
import {
  formatMoney,
  getAddToBagText,
  getEntityImage,
  getPurchaseRewardAmountForProduct,
  isProductEligibleForCheckout,
  isProductOutOfStock,
  isShopifyProduct,
} from '../../utils';
import { TABLET_SCREEN_SIZE } from '../../utils/constants';
import { getBuyOrViewProductMetaTag } from '../../utils/dataLayer';
import { Button } from '../Button';
import { BuyBoxType } from './BuyBoxType';

interface ProductPriceListProps {
  products: ProductRewardSearchResult[];
  onBuyClick: (product: ProductRewardSearchResult) => void;
  onOpenRetailerProductPageClick: (product: ProductRewardSearchResult) => void;
  onExpandBuyBoxToggle?: () => void;
  isExpanded?: boolean;
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both';
  disabledCheckout?: boolean;
  zeroPadding?: boolean;
}

const ProductPriceList = ({
  products,
  onBuyClick,
  onOpenRetailerProductPageClick,
  onExpandBuyBoxToggle,
  isExpanded,
  checkoutAllowedOfferType,
  disabledCheckout,
  zeroPadding,
}: ProductPriceListProps) => {
  const theme = useTheme();
  const dialogTitle = (theme as any).content?.buyBoxDialog?.title || 'Ways to buy';
  const viewButtonText = (theme as any).content?.buyBoxDialog?.viewButtonText;

  const getBuyBoxButtonText = (item: any) => {
    const defaultText = getAddToBagText(item, checkoutAllowedOfferType, disabledCheckout);
    if (viewButtonText && defaultText === 'View') {
      return viewButtonText;
    }
    return defaultText;
  };

  const removeNonLiveCampaigns = (campaign: any[]) => {
    return campaign.filter((c) => c.campaignStatus === 'live');
  };

  return (
    <Details zeroPadding={zeroPadding}>
      <div className="product_price" style={isExpanded && !zeroPadding ? { padding: '15px 0 15px', gap: 20 } : {}}>
        {theme?.dtcMode && (
          <>
            <div className="detail_title">{dialogTitle}</div>
          </>
        )}
        {products.map((item: any, idx: number) => {
          let hasActiveInstantReward = false;

          for (const campaignReward of removeNonLiveCampaigns(item?.campaignRewards ?? [])) {
            if (
              campaignReward?.campaignType === CampaignType.DailyDealsRewardCampaign ||
              campaignReward?.rewardUnlockStrategy === RewardUnlockStrategy.Instant
            ) {
              hasActiveInstantReward = true;
              break;
            }
          }
          const rewardAmount = getPurchaseRewardAmountForProduct(item);
          const rewardPercent = !!item.retailPrice && !!rewardAmount ? (rewardAmount * 100) / item.retailPrice : 0;

          const shopifyProduct = isShopifyProduct(item?.retailerId);
          const isOutOfStock = isProductOutOfStock(item, checkoutAllowedOfferType);

          return (
            <div key={idx} className="price_card">
              <div
                className="retailer_icon"
                onClick={() => {
                  onExpandBuyBoxToggle?.();
                }}
              >
                <img alt="retailer" src={item.retailerShortLogo ?? getEntityImage(item.retailerId, 'retailer')}></img>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div className="detail_retailer_price">
                    <a
                      href={item.retailerProductPageUrl}
                      target="_blank"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenRetailerProductPageClick(item);
                      }}
                      style={{ textDecoration: 'none', color: theme.vars.palette.text.primary }}
                    >
                      <div className="name" style={{}}>
                        {item.retailerDisplayName || ''}
                      </div>
                    </a>
                    <div className="price">{formatMoney(item.retailPrice)}</div>
                  </div>
                  {isOutOfStock && shopifyProduct ? null : (
                    <Button
                      increaseSpecificity
                      variant="solid"
                      color="primary"
                      height={{ xs: 32, md: 35 }}
                      sx={{
                        minWidth: 82,
                        width: 'fit-content',
                        margin: '10px 0',
                        [`@media screen and (max-width: 400px)`]: {
                          maxWidth: 200,
                        },
                      }}
                      className="bc-dialog-buy-button"
                      {...getBuyOrViewProductMetaTag(
                        isProductEligibleForCheckout(item, checkoutAllowedOfferType) && !disabledCheckout
                          ? 'buy'
                          : 'view',
                        item.retailerDisplayName || '',
                        item.name,
                        item.id,
                        item.stacklineSku,
                        window.location.href,
                        item.retailPrice,
                      )}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isProductEligibleForCheckout(item, checkoutAllowedOfferType) && !disabledCheckout) {
                          onBuyClick(item);
                        } else {
                          onOpenRetailerProductPageClick(item);
                        }
                      }}
                    >
                      {getBuyBoxButtonText(item)}
                    </Button>
                  )}
                </div>
                {isOutOfStock ? <OutOfStockPill pillContainerStyle={{}} /> : null}
                {rewardAmount && !isOutOfStock ? (
                  <RewardPillWithText
                    pillContainerStyle={isExpanded ? { padding: '1px 6.5px', fontSize: 11 } : {}}
                    rewardAmount={rewardAmount}
                    rewardPercent={rewardPercent}
                    rewardCampaignType={
                      hasActiveInstantReward
                        ? CampaignType.DailyDealsRewardCampaign
                        : CampaignType.PurchaseRewardCampaign
                    }
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Details>
  );
};

export interface ProductPriceProps {
  productsToList: ProductRewardSearchResult[];
  onBuyClick: (product: ProductRewardSearchResult) => void;
  onOpenRetailerProductPageClick: (product: ProductRewardSearchResult) => void;
  onExpandBuyBoxToggle?: () => void;
  buyBoxType?: BuyBoxType;
  fixedPosition?: boolean;
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both';
  disabledCheckout?: boolean;
  zeroPadding?: boolean;
}

export const ProductPrice = ({
  productsToList,
  onOpenRetailerProductPageClick,
  onBuyClick,
  buyBoxType,
  onExpandBuyBoxToggle: _onExpandBuyBoxToggle,
  checkoutAllowedOfferType,
  disabledCheckout,
  zeroPadding,
  ...props
}: ProductPriceProps) => {
  const theme = useTheme();

  if (!productsToList?.length) {
    return null;
  }

  const showStickyBuyBox =
    buyBoxType === BuyBoxType.BuyBoxBottomSheetShort ||
    buyBoxType === BuyBoxType.BuyBoxBottomSheetExpanded ||
    buyBoxType === BuyBoxType.BuyBoxBottomSheetAlwaysExpanded;

  const hideBackdrop = buyBoxType === BuyBoxType.BuyBoxBottomSheetAlwaysExpandedWithoutBackdrop;

  const canExpand = productsToList?.length > 1 && buyBoxType === BuyBoxType.BuyBoxBottomSheetShort;
  const isExpanded =
    buyBoxType === BuyBoxType.BuyBoxBottomSheetExpanded ||
    buyBoxType === BuyBoxType.BuyBoxBottomSheetAlwaysExpanded ||
    buyBoxType === BuyBoxType.BuyBoxBottomSheetAlwaysExpandedWithoutBackdrop;

  const fixedPosition = props.fixedPosition || buyBoxType === BuyBoxType.BuyBoxBottomSheetExpanded;
  const onExpandBuyBoxToggle = () => {
    if (canExpand || isExpanded) {
      _onExpandBuyBoxToggle?.();
    }
  };

  useEffect(() => {
    const overflow = document.body.style.overflow;
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = overflow;
    }
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isExpanded]);

  if (showStickyBuyBox) {
    return (
      <FixedToBottomDetails
        hideBackdrop={hideBackdrop}
        isExpanded={isExpanded}
        fixedPosition={fixedPosition}
        expandToggle={onExpandBuyBoxToggle}
      >
        {isExpanded ? (
          <ProductPriceList
            products={productsToList}
            onBuyClick={onBuyClick}
            onOpenRetailerProductPageClick={onOpenRetailerProductPageClick}
            onExpandBuyBoxToggle={onExpandBuyBoxToggle}
            isExpanded={isExpanded}
            checkoutAllowedOfferType={checkoutAllowedOfferType}
            disabledCheckout={disabledCheckout}
            zeroPadding={zeroPadding}
          />
        ) : (
          <Details zeroPadding={zeroPadding}>
            <div className="product_price" style={{ padding: 0, paddingBottom: 5 }}>
              {[productsToList[0]].map((item: any, idx: number) => {
                let hasActiveInstantReward = item.hasActiveInstantReward;
                for (const campaignReward of (item?.campaignRewards ?? []).filter(
                  (c: any) => c.campaignStatus === 'live',
                )) {
                  if (
                    campaignReward?.campaignType === CampaignType.DailyDealsRewardCampaign ||
                    campaignReward?.rewardUnlockStrategy === RewardUnlockStrategy.Instant
                  ) {
                    hasActiveInstantReward = true;
                    break;
                  }
                }
                const rewardPercent =
                  !!item.retailPrice && !!item?.totalRewardAmount
                    ? (item.totalRewardAmount * 100) / item.retailPrice
                    : 0;

                return (
                  <div key={idx} className="price_card" style={{ flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ display: 'flex', width: '100%' }}>
                      <div
                        style={{ marginRight: 7 }}
                        className="retailer_icon"
                        onClick={() => {
                          onExpandBuyBoxToggle?.();
                        }}
                      >
                        <img
                          alt="retailer"
                          src={item.retailerShortLogo ?? getEntityImage(item.retailerId, 'retailer')}
                        ></img>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {canExpand ? (
                            <ArrowBackIosNewIcon
                              style={{ transform: 'rotate(90deg) scale(0.5)' }}
                              onClick={() => {
                                onExpandBuyBoxToggle?.();
                              }}
                            />
                          ) : null}
                          <Button
                            height={{ md: 44 }}
                            disableMaxWidth
                            fullWidth
                            sx={{
                              backgroundColor: theme.vars.palette.primary.main,
                              fontWeight: 600,
                              margin: '5px 0 10px 10px',
                            }}
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (isProductEligibleForCheckout(item, checkoutAllowedOfferType) && !disabledCheckout) {
                                onBuyClick(item);
                              } else {
                                onOpenRetailerProductPageClick(item);
                              }
                            }}
                          >
                            {`${getAddToBagText(item, checkoutAllowedOfferType, disabledCheckout)}` +
                              (item.retailPrice !== undefined ? '  \u2022  ' + formatMoney(item.retailPrice) : '')}
                          </Button>
                        </div>
                      </div>
                    </div>
                    {item?.totalRewardAmount ? (
                      <RewardPillWithText
                        containerStyle={{ marginLeft: 19 }}
                        rewardAmount={item.totalRewardAmount}
                        rewardPercent={rewardPercent}
                        pillContainerStyle={isExpanded ? { height: 22, width: '60px' } : {}}
                        rewardCampaignType={
                          hasActiveInstantReward
                            ? CampaignType.DailyDealsRewardCampaign
                            : CampaignType.PurchaseRewardCampaign
                        }
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </Details>
        )}
      </FixedToBottomDetails>
    );
  }
  return (
    <ProductPriceList
      products={productsToList}
      onBuyClick={onBuyClick}
      onOpenRetailerProductPageClick={onOpenRetailerProductPageClick}
      onExpandBuyBoxToggle={onExpandBuyBoxToggle}
      isExpanded={isExpanded}
      checkoutAllowedOfferType={checkoutAllowedOfferType}
      zeroPadding={zeroPadding}
    />
  );
};

const Details = styled('div', {
  label: 'ProductPriceDetails',
  shouldForwardProp: (prop) => prop !== 'zeroPadding',
})<{ zeroPadding?: boolean }>(({ theme, zeroPadding }) => ({
  '.product_price': {
    padding: zeroPadding ? '0' : '40px 35px',
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      padding: zeroPadding ? '0' : '40px 0px',
      paddingTop: zeroPadding ? '0' : 0,
      width: '100%',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: zeroPadding ? 18 : 30,
    '.bar': {
      display: 'block !important',
      width: '31px !important',
      height: '4px !important',
      borderRadius: '10px !important',
      backgroundColor: '#d8d8d8 !important',
      margin: '0 auto -2.5px',
      marginTop: '-8px',
    },
    '.detail_title': {
      color: theme.vars.palette.text.primary,
      margin: 'unset',
      fontSize: '22px !important',
      fontWeight: '600 !important ',
      textAlign: 'center !important',
    },
    '.price_card': {
      display: 'flex',
      '.retailer_icon': {
        width: '50px',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '20px',
        marginTop: '5px',
        img: {
          maxWidth: '100%',
          maxHeight: '100%',
        },
      },
      '.detail_retailer_price': {
        flex: 1,
        marginBottom: 5,
        div: {
          width: 'fit-content',
        },
        '.name': {
          fontSize: 14,
          fontWeight: 600,
        },
        '.price': {
          fontSize: 24,
          fontWeight: 600,
          color: theme.vars.palette.text.primary,
          [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
            fontSize: 22,
          },
        },
      },
    },
  },
}));

const FixedToBottomDetails = ({
  children,
  isExpanded,
  expandToggle,
  hideBackdrop,
  fixedPosition,
}: {
  isExpanded: boolean;
  expandToggle?: () => void;
  hideBackdrop?: boolean;
  children: React.ReactNode | React.ReactNode[];
  fixedPosition?: boolean;
}) => {
  const theme = useTheme();
  const FixedComponent = ({ children: ch }: { children: React.ReactNode | React.ReactNode[] }) => (
    <>
      {hideBackdrop ? null : (
        <Backdrop
          open={isExpanded}
          onClick={() => {
            expandToggle?.();
          }}
        />
      )}
      <div
        style={{
          position: fixedPosition ? 'fixed' : 'relative',
          zIndex: 901,
          width: '100%',
          padding: '10px 20px 18px 20px',
          bottom: 0,
          left: 0,
          background: theme.vars.palette.background.paper,
          borderTop: `1px solid ${theme.vars.palette.divider}`,
          boxSizing: 'border-box',
          minHeight: 121,
          transition: 'all 0.3s ease-in-out',
          borderTopLeftRadius: isExpanded ? 20 : 0,
          borderTopRightRadius: isExpanded ? 20 : 0,
        }}
      >
        {ch}
      </div>
    </>
  );
  return isExpanded && !theme?.dtcMode ? (
    <FixedComponent>
      <>
        <Icon
          sx={(theme) => ({
            paddingTop: 0,
            float: 'right',
            cursor: 'pointer',
            color: theme.vars.palette.text.primary,
          })}
          className="close"
          onClick={() => {
            expandToggle?.();
          }}
        >
          <CloseIcon />
        </Icon>
        {children}
      </>
    </FixedComponent>
  ) : (
    <FixedComponent> {children} </FixedComponent>
  );
};
